'use client';

import { faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import type { Module } from '~/data/modules/types';
import Highlighter from '../../AnimatedComponents/Highlighter';
import ButtonLink from '../../ButtonLink';

type RelatedModuleItem = Pick<Module, 'slug' | 'title' | 'seo' | 'image' | 'functions'>;

interface RelatedModulesProps {
    modules: RelatedModuleItem[];
    title?: string;
    description?: string;
}

export function RelatedModules({
    modules,
    title = 'Verwandte Module',
    description = 'Kombiniere dieses Modul mit weiteren Sportision-Bausteinen für einen durchgängigen digitalen Workflow.'
}: RelatedModulesProps) {
    if (!modules.length) return null;

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05,
                delayChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -10 },
        visible: { opacity: 1, x: 0 }
    };

    return (
        <section className="tw-relative tw-overflow-hidden tw-rounded-xl tw-p-6 md:tw-p-8">
            <motion.h2
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="tw-mb-2 tw-text-2xl tw-font-bold tw-text-accents-9"
            >
                <Highlighter strokeWidth={2} iterations={2} brackets={['left', 'right']} action="bracket">
                    {title}
                </Highlighter>
            </motion.h2>
            <motion.p
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="tw-mb-8 tw-text-sm tw-text-accents-6"
            >
                {description}
            </motion.p>

            <div className="tw-grid tw-gap-6 md:tw-grid-cols-2">
                {modules.map((module, idx) => {
                    const imageUrl = module.image; // adjust based on your data structure

                    return (
                        <motion.article
                            key={module.slug}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.4, delay: idx * 0.1 }}
                            className="tw-card tw-card-bordered tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-p-0 tw-transition-all tw-duration-300"
                        >
                            {/* Image section (if available) */}
                            {imageUrl && (
                                <div className="tw-relative tw-h-[250px] tw-overflow-hidden tw-bg-accents-1">
                                    <motion.div
                                        className="tw-absolute tw-inset-0 tw-bg-cover tw-bg-center"
                                        style={{ backgroundImage: `url(${imageUrl})` }}
                                        animate={{ scale: 1 }}
                                        transition={{ duration: 0.4 }}
                                    />
                                    <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/40 tw-to-transparent" />
                                </div>
                            )}

                            {/* Content */}
                            <div className="tw-relative tw-flex tw-flex-1 tw-flex-col tw-p-5 md:tw-p-6">
                                <h3 className="tw-mb-3 tw-pr-6 tw-text-lg tw-font-semibold tw-text-accents-9 md:tw-text-xl">
                                    {module.title}
                                </h3>

                                <motion.div
                                    variants={containerVariants}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    className="tw-mb-4 tw-mt-auto tw-space-y-2"
                                >
                                    {module.functions.slice(0, 5).map((feature, featureIdx) => (
                                        <motion.div
                                            key={`${module.slug}-feature-${featureIdx}`}
                                            variants={itemVariants}
                                            className="tw-flex tw-items-center tw-gap-2 tw-text-sm"
                                        >
                                            <FontAwesomeIcon icon={faCheck} className="tw-text-success/80" />
                                            <p className="tw-m-0 tw-font-medium tw-leading-relaxed tw-text-accents-8">
                                                {feature.name}
                                            </p>
                                        </motion.div>
                                    ))}
                                    <motion.div
                                        variants={itemVariants}
                                        className="tw-flex tw-items-center tw-gap-2 tw-text-sm"
                                    >
                                        <FontAwesomeIcon icon={faCheck} className="tw-text-success/80" />
                                        <p className="tw-m-0 tw-font-medium tw-leading-relaxed tw-text-accents-8">
                                            Und Vieles mehr...
                                        </p>
                                    </motion.div>
                                </motion.div>

                                <div className="tw-mt-4">
                                    <ButtonLink href={`/${module.slug}`}>
                                        <span>Modul entdecken</span>
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </ButtonLink>
                                </div>
                            </div>
                        </motion.article>
                    );
                })}
            </div>
        </section>
    );
}
